import { useMemo } from 'react';
import { CommonUtility, units, unitsTextMap } from '../utility';
import { conversionMap, ConversionUtility } from '../utility/conversions';
import { MuteText } from '../elements';

interface IConversionViewerProps {
  brackets?: boolean;
  fullText?: boolean;
  hideOnNoConversion?: boolean;
  imperial?: boolean;
  originalUnit?: string;
  showUnit?: boolean;
  value?: number | string;
}

type ConversionFunction = (value: number | string) => number;

interface IConversionFunctionsMap {
  [key: string]: ConversionFunction;
}

export function ConversionViewer({
  brackets = false,
  fullText = false,
  hideOnNoConversion = false,
  imperial,
  originalUnit,
  showUnit = true,
  value,
}: IConversionViewerProps): JSX.Element | string {
  const conversionFunctionsMap: IConversionFunctionsMap = {
    [units.liters]: ConversionUtility.litersToGallons,
    [units.milliLiters]: ConversionUtility.millilitersToFluidOunces,
    [units.kiloGrams]: ConversionUtility.kiloToPound,
    [units.grams]: ConversionUtility.gramsToOunces,
    [units.centimeters]: ConversionUtility.centiToInch,
    [units.gallons]: ConversionUtility.gallonsToLiters,
    [units.fluidOunces]: ConversionUtility.fluidOuncesToMilliliters,
    [units.pounds]: ConversionUtility.poundToKilo,
    [units.ounces]: ConversionUtility.ouncesToGrams,
    [units.inches]: ConversionUtility.inchToCenti,
  };

  const needsConversion = useMemo(() => {
    if (originalUnit === units.units || !originalUnit) return false;

    if (imperial) {
      if (
        ![
          units.fluidOunces,
          units.ounces,
          units.pounds,
          units.gallons,
          units.inches,
        ].includes(originalUnit)
      ) {
        return true;
      }
    } else if (
      ![
        units.milliLiters,
        units.grams,
        units.kiloGrams,
        units.liters,
        units.centimeters,
      ].includes(originalUnit)
    ) {
      return true;
    }
    return false;
  }, [imperial, originalUnit]);

  const convertedValue = useMemo(() => {
    let toReturn: number | string = Number(value || 0);
    if (needsConversion && originalUnit) {
      const calculated = conversionFunctionsMap[originalUnit]?.(value || 0);
      if (calculated !== undefined) {
        toReturn = calculated;
      }
    }
    toReturn = CommonUtility.numberWithCommas(CommonUtility.roundNumber(toReturn));
    return toReturn;
  }, [needsConversion, value, originalUnit, conversionFunctionsMap]);

  const convertedUnit = useMemo(() => {
    let calUnit = originalUnit || '';
    if (needsConversion && originalUnit) {
      calUnit = conversionMap[originalUnit];
    }

    if (fullText) {
      calUnit = unitsTextMap[calUnit] || calUnit;
    }

    return calUnit;
  }, [needsConversion, originalUnit, fullText]);

  if (!value) return '';

  if (!originalUnit) return String(value);

  if (!needsConversion) {
    if (hideOnNoConversion) {
      return '';
    }

    return (
      <>
        {!!brackets && '('}
        {convertedValue} {showUnit && <MuteText>{convertedUnit || 'pieces'}</MuteText>}
        {!!brackets && ')'}
      </>
    );
  }

  return (
    <>
      {!!brackets && '('}
      {convertedValue} {showUnit && <MuteText>{convertedUnit || 'pieces'}</MuteText>}
      {!!brackets && ')'}
    </>
  );
}

interface IConversionUnitViewerProps {
  imperial?: boolean;
  originalUnit?: string;
  fullText?: boolean;
}

export function ConversionUnitViewer({
  imperial,
  originalUnit,
  fullText = false,
}: IConversionUnitViewerProps): string {
  const needsConversion = useMemo(() => {
    if (originalUnit === units.units || !originalUnit) return false;

    if (imperial) {
      if (
        ![
          units.fluidOunces,
          units.ounces,
          units.pounds,
          units.gallons,
          units.inches,
        ].includes(originalUnit)
      ) {
        return true;
      }
    } else if (
      ![
        units.milliLiters,
        units.grams,
        units.kiloGrams,
        units.liters,
        units.centimeters,
      ].includes(originalUnit)
    ) {
      return true;
    }
    return false;
  }, [imperial, originalUnit]);

  const convertedUnit = useMemo(() => {
    let calUnit = originalUnit || '';
    if (needsConversion && originalUnit) {
      calUnit = conversionMap[originalUnit];
    }

    if (fullText) {
      calUnit = unitsTextMap[calUnit] || calUnit;
    }

    return calUnit;
  }, [needsConversion, originalUnit, fullText]);

  if (!originalUnit) return '';
  if (!needsConversion) {
    return convertedUnit;
  }

  return convertedUnit;
}
